import React, { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { SettingsProvider } from "./context/SettingsContext";
import { DataRefreshProvider } from "./context/DataRefreshContext";
import { Toaster } from "./components/ui/sonner";
import axios from "axios";

// Pages
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import PropertiesPage from "./pages/PropertiesPage";
import AgentsPage from "./pages/AgentsPage";
import SalesPage from "./pages/SalesPage";
import BonusTrackerPage from "./pages/BonusTrackerPage";
import InvoicesPage from "./pages/InvoicesPage";
import SettingsPage from "./pages/SettingsPage";
import CustomersPage from "./pages/CustomersPage";
import { PageLoader } from "./components/Loading";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Protected Route Component
const ProtectedRoute = ({ children, mdOnly = false }) => {
  const { isAuthenticated, loading, isMD } = useAuth();

  if (loading) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (mdOnly && !isMD) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// App Routes Component
const AppRoutes = () => {
  const { isAuthenticated, loading } = useAuth();
  const [seeding, setSeeding] = useState(false);

  useEffect(() => {
    // Seed data on first load
    const seedData = async () => {
      setSeeding(true);
      try {
        await axios.post(`${API}/seed`);
        console.log("Seed data initialized");
      } catch (error) {
        // Ignore if already seeded
        console.log("Seed check:", error.response?.data?.message || "Data exists");
      } finally {
        setSeeding(false);
      }
    };
    seedData();
  }, []);

  if (loading || seeding) {
    return (
      <div className="min-h-screen bg-lux-black flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-lux-border border-t-lux-gold rounded-full animate-spin mx-auto mb-4" />
          <p className="text-lux-muted">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <LoginPage />}
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/properties"
        element={
          <ProtectedRoute>
            <PropertiesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/agents"
        element={
          <ProtectedRoute>
            <AgentsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales"
        element={
          <ProtectedRoute>
            <SalesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/bonus-tracker"
        element={
          <ProtectedRoute mdOnly>
            <BonusTrackerPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/customers"
        element={
          <ProtectedRoute>
            <CustomersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/invoices"
        element={
          <ProtectedRoute>
            <InvoicesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/invoices/:saleId"
        element={
          <ProtectedRoute>
            <InvoicesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute mdOnly>
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <SettingsProvider>
            <DataRefreshProvider>
              <AppRoutes />
              <Toaster
                position="top-right"
                toastOptions={{
                  style: {
                    background: '#0a1428',
                    border: '1px solid #1e2a45',
                    color: '#e8dfc8',
                  },
                }}
              />
            </DataRefreshProvider>
          </SettingsProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
